import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { MockUnit, MockSite } from '../../utils/mockData';
import { UnitType } from '../../types/index';
import { computeWaterDepthCm, computePercentFilled, waterReadingGapLabel } from '@shared/metrics';
import 'leaflet/dist/leaflet.css';
import './SiteMap.css';

interface SiteMapProps {
  units: MockUnit[];
  sites: MockSite[];
  activeSiteId?: string;
}

// Helper component to programmatically fit map bounds
function ChangeView({ bounds, center }: { bounds: L.LatLngBounds | null; center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    } else {
      map.setView(center, 13);
    }
  }, [bounds, center, map]);
  return null;
}

// Custom Leaflet DivIcon creator to avoid default marker asset issues in bundlers
// Renders a solid color circle for the marker
const createCustomIcon = () => {
  return L.divIcon({
    html: `<div class="custom-marker marker-info"></div>`,
    className: 'custom-marker-wrapper',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
    popupAnchor: [0, -10]
  });
};

export function SiteMap({ units, sites, activeSiteId }: SiteMapProps) {
  // Determine center coordinates
  let center: [number, number] = [51.0447, -114.0719]; // Default Calgary
  
  if (activeSiteId) {
    const activeSite = sites.find(s => s.id === activeSiteId);
    if (activeSite) {
      center = [activeSite.location.lat, activeSite.location.lng];
    }
  } else if (sites.length > 0) {
    // Center on the first site
    center = [sites[0].location.lat, sites[0].location.lng];
  }

  // Find units that have location coordinates
  const unitsWithLocation = units.map(unit => {
    const parentSite = sites.find(s => s.id === unit.siteId);
    if (!parentSite) return null;

    // Prefer the unit's own configured location; fall back to the parent site with a small
    // offset to prevent multiple units at the same site from stacking exactly on top of each other
    const hasOwnLocation = unit.location && (unit.location.lat !== 0 || unit.location.lng !== 0);
    const lat = hasOwnLocation
      ? unit.location!.lat
      : parentSite.location.lat + (unit.nodeId % 5 - 2) * 0.0008;
    const lng = hasOwnLocation
      ? unit.location!.lng
      : parentSite.location.lng + (unit.nodeId % 4 - 2) * 0.0008;

    return {
      ...unit,
      lat,
      lng,
      siteName: parentSite.name
    };
  }).filter((u): u is NonNullable<typeof u> => u !== null);

  // Calculate bounds for all units
  let bounds: L.LatLngBounds | null = null;
  if (unitsWithLocation.length > 0) {
    bounds = L.latLngBounds(unitsWithLocation.map(u => [u.lat, u.lng]));
  }

  return (
    <div className="site-map-container">
      <MapContainer 
        center={center} 
        zoom={13} 
        scrollWheelZoom={true} 
        className="leaflet-map"
      >
        <ChangeView bounds={bounds} center={center} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {unitsWithLocation.map((unit) => (
          <Marker 
            key={unit.unitId} 
            position={[unit.lat, unit.lng]}
            icon={createCustomIcon()}
          >
            <Popup>
              <div className="map-popup-content">
                <Link to={`/dashboard/units/${unit.unitId}`} className="popup-title hyperlink">
                  {unit.nodeName}
                </Link>
                <div className="popup-meta">
                  <span>Site: {unit.siteName}</span>
                  <span>ID: {unit.nodeId} | Type: {unit.unitType === UnitType.MobileStation ? 'Mobile' : 'Repeater'}</span>
                </div>
                
                <div className="popup-metrics">
                  {unit.unitType === UnitType.MobileStation && (() => {
                    // Shared metric math (single source of truth) — the previous
                    // inline copy had drifted and could show >100% (D5).
                    let percentFilled: number | null = null;
                    if (unit.currentLevel !== undefined) {
                      const depthCm = computeWaterDepthCm(unit.currentLevel, unit);
                      if (depthCm !== null) {
                        const p = computePercentFilled(depthCm, unit);
                        percentFilled = p === null ? null : Math.round(p);
                      }
                    }

                    return (
                      <div className="popup-metric-item">
                        <span className="popup-metric-label">Water Level:</span>
                        <strong className="popup-metric-value">
                          {percentFilled !== null ? `${percentFilled}%` : waterReadingGapLabel(unit, unit.currentLevel)}
                        </strong>
                      </div>
                    );
                  })()}
                  <div className="popup-metric-item">
                    <span className="popup-metric-label">Battery:</span>
                    <strong className="popup-metric-value">{unit.currentVoltage.toFixed(2)} V</strong>
                  </div>
                </div>

                <Link to={`/dashboard/units/${unit.unitId}`} className="popup-link hyperlink">
                  Go to Data Details →
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
